import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { updateServiceEmployeeSaleTracking } from '@/modules/Service/services'
import { Service } from '@/modules/Service/types'
import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface EditEmployeeSaleTrackingModalProps extends ModalProps {
    serviceID: number
    service: Service
}

interface FormData {
    employee_sale_tracking_ids: number[]
}

const EditEmployeeSaleTrackingModal: React.FC<
    EditEmployeeSaleTrackingModalProps
> = ({ open, onClose, serviceID, service }) => {
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        pattern: 'services',
        mode: 'contains',
    })

    const formControl = useForm<FormData>({
        defaultValues: {
            employee_sale_tracking_ids:
                service?.employee_sale_tracking_ids || [],
        },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: ({
                serviceID,
                data,
            }: {
                serviceID: number
                data: FormData
            }) =>
                updateServiceEmployeeSaleTracking({
                    serviceID,
                    employee_sale_tracking_ids: data.employee_sale_tracking_ids,
                }),
        },
        {
            successMessage: t('update-employee-sale-tracking-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
            onSuccess: () => {
                refetch()
                onClose()
            },
        }
    )

    const handleSubmit = async (values: FormData) => {
        mutateAsync({ serviceID, data: values })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('edit-employee-sale-tracking')}
        >
            <GridHookForm
                onSubmit={handleSubmit}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        handler: onClose,
                        disabled: isPending,
                        variant: 'outlined',
                    },
                    {
                        title: t('submit'),
                        type: 'submit',
                        disabled: isPending,
                        loading: isPending,
                        variant: 'contained',
                    },
                ]}
                data={[
                    {
                        className: 'col-span-4',
                        renderEditComponent: () => (
                            <EmployeeSelect
                                control={formControl.control}
                                name="employee_sale_tracking_ids"
                                label={t('services.commissions')}
                                multiple
                            />
                        ),
                    },
                ]}
                formControl={formControl}
            />
        </Dialog>
    )
}

export default EditEmployeeSaleTrackingModal
